import { useEffect } from 'react';
import type { Locale } from '../i18n';
import { DEFAULT_LOCALE, LOCALE_PREFIXES, stripLocale, withLocale } from './localePath';

export const SITE_URL = 'https://codexalgerie.com';
export const SITE_NAME = 'Codex Algeria';
export const DEFAULT_OG = `${SITE_URL}/og.png`;

export type SeoInput = {
  title: string;
  description: string;
  path: string;
  type?: 'website' | 'article';
  image?: string;
  imageAlt?: string;
  noindex?: boolean;
  locale?: Locale;
  publishedTime?: string;
  modifiedTime?: string;
  keywords?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

function absoluteUrl(path: string) {
  if (path.startsWith('http')) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertMetaList(attr: 'name' | 'property', key: string, values: string[]) {
  document.head.querySelectorAll(`meta[${attr}="${key}"]`).forEach((n) => n.remove());
  values.forEach((content) => {
    const el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute('content', content);
    document.head.appendChild(el);
  });
}

function upsertLink(rel: string, href: string, extra?: Record<string, string>) {
  const selector = extra?.hreflang
    ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (extra) {
      Object.entries(extra).forEach(([k, v]) => el!.setAttribute(k, v));
    }
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(data: Record<string, unknown> | Record<string, unknown>[] | undefined) {
  const id = 'codex-page-jsonld';
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!data) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

const OG_LOCALE: Record<Locale, string> = {
  en: 'en_US',
  fr: 'fr_DZ',
  ar: 'ar_DZ',
};

export function applySeo(input: SeoInput) {
  const url = absoluteUrl(input.path);
  const image = input.image ?? DEFAULT_OG;
  const locale = input.locale ?? 'en';

  document.title = input.title;

  upsertMeta('name', 'description', input.description);
  upsertMeta(
    'name',
    'robots',
    input.noindex
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  );
  if (input.keywords) upsertMeta('name', 'keywords', input.keywords);

  upsertLink('canonical', url);
  const bare = stripLocale(input.path);
  for (const loc of LOCALE_PREFIXES) {
    upsertLink('alternate', absoluteUrl(withLocale(loc, bare)), { hreflang: loc });
  }
  // Regional variants — explicit Algeria targeting
  upsertLink('alternate', absoluteUrl(withLocale('fr', bare)), { hreflang: 'fr-DZ' });
  upsertLink('alternate', absoluteUrl(withLocale('ar', bare)), { hreflang: 'ar-DZ' });
  upsertLink('alternate', absoluteUrl(withLocale(DEFAULT_LOCALE, bare)), {
    hreflang: 'x-default',
  });

  upsertMeta('property', 'og:type', input.type ?? 'website');
  upsertMeta('property', 'og:site_name', SITE_NAME);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:title', input.title);
  upsertMeta('property', 'og:description', input.description);
  upsertMeta('property', 'og:image', image);
  upsertMeta('property', 'og:image:alt', input.imageAlt ?? input.title);
  upsertMeta('property', 'og:locale', OG_LOCALE[locale]);
  upsertMetaList(
    'property',
    'og:locale:alternate',
    (['en_US', 'fr_DZ', 'ar_DZ'] as const).filter((l) => l !== OG_LOCALE[locale])
  );

  if (input.publishedTime) {
    upsertMeta('property', 'article:published_time', input.publishedTime);
  }
  if (input.modifiedTime) {
    upsertMeta('property', 'article:modified_time', input.modifiedTime);
  }

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', input.title);
  upsertMeta('name', 'twitter:description', input.description);
  upsertMeta('name', 'twitter:image', image);

  setJsonLd(input.jsonLd);
}

export function useSeo(input: SeoInput) {
  useEffect(() => {
    applySeo(input);
    return () => {
      // Keep last tags; next page will overwrite. Clear page-level JSON-LD on leave.
      setJsonLd(undefined);
    };
  }, [
    input.title,
    input.description,
    input.path,
    input.type,
    input.image,
    input.imageAlt,
    input.noindex,
    input.locale,
    input.publishedTime,
    input.modifiedTime,
    input.keywords,
    JSON.stringify(input.jsonLd),
  ]);
}

export function orgId() {
  return `${SITE_URL}/#organization`;
}

export function breadcrumbJsonLd(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export function blogPostingJsonLd(opts: {
  title: string;
  description: string;
  path: string;
  date: string;
  locale: Locale;
  tags?: string[];
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: opts.title,
    description: opts.description,
    datePublished: opts.date,
    dateModified: opts.date,
    inLanguage: opts.locale,
    mainEntityOfPage: absoluteUrl(opts.path),
    url: absoluteUrl(opts.path),
    image: DEFAULT_OG,
    keywords: opts.tags?.join(', '),
    author: { '@id': orgId() },
    publisher: { '@id': orgId() },
    isPartOf: {
      '@type': 'Blog',
      '@id': `${SITE_URL}/fr/blog#blog`,
      name: 'Codex Field Notes',
      url: `${SITE_URL}/fr/blog`,
    },
  };
}

export function creativeWorkJsonLd(opts: {
  title: string;
  description: string;
  path: string;
  year: string;
  tags?: string[];
  locale: Locale;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: opts.title,
    description: opts.description,
    url: absoluteUrl(opts.path),
    dateCreated: opts.year,
    inLanguage: opts.locale,
    keywords: opts.tags?.join(', '),
    creator: { '@id': orgId() },
    provider: { '@id': orgId() },
    about: opts.tags,
  };
}

export function collectionJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  items: { name: string; path: string }[];
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: opts.name,
    description: opts.description,
    url: absoluteUrl(opts.path),
    isPartOf: { '@id': `${SITE_URL}/#website` },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: opts.items.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.name,
        url: absoluteUrl(item.path),
      })),
    },
  };
}

export function faqJsonLd(faqs: readonly { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };
}

export function webPageJsonLd(opts: {
  name: string;
  description: string;
  path: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': absoluteUrl(`${opts.path}#webpage`),
    url: absoluteUrl(opts.path),
    name: opts.name,
    description: opts.description,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': orgId() },
    publisher: { '@id': orgId() },
  };
}
